import type { BetterFetch } from "@better-fetch/fetch";
import type { Store } from "@di-devs/better-feature/types";
import { atom } from "nanostores";
import { memberChatEndpointContract } from "../../shared/contracts/memberChat.contracts";
import type { Chat } from "../../shared/types/memberChat.types";
import type {
	GetChatQueryParams,
	ReplyChatRequest,
} from "../../shared/types/memberChatApi.types";
import type { Member } from "../../shared/types/memberCore.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

export function createMemberChatPlugin($fetch: BetterFetch, $store?: Store) {
	const querySets = createQuerySetMap(memberChatEndpointContract, $fetch);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			$chatMessages: atom<Chat[] | null>(null),
			$selectedChatMember: atom<Member | null>(null),
			$unreadCount: atom<number>(0),
			$isChatModalVisible: atom<boolean>(false),
			$chatLoading: atom<boolean>(false),
			$chatError: atom<string | null>(null),
		},
		actions: {
			async getChat(
				memberId: Member["memberId"],
				queryParams?: GetChatQueryParams,
			) {
				const getChatPath = memberChatEndpointContract.getChat.path.replace(
					":memberId",
					String(memberId),
				);
				const searchParams = new URLSearchParams();

				if (queryParams?.sort) searchParams.append("sort", queryParams.sort);

				const fullPath = searchParams.toString()
					? `${getChatPath}?${searchParams.toString()}`
					: getChatPath;

				const chatResult = await $fetch<
					typeof memberChatEndpointContract.getChat.responseType
				>(fullPath, {
					method: memberChatEndpointContract.getChat.method,
				});

				// Update the chat messages store with the fetched data
				if (chatResult.data?.chats) {
					$store?.atoms?.$chatMessages.set(chatResult.data.chats);
				}

				return chatResult;
			},
			async replyChat(memberId: Member["memberId"], data: ReplyChatRequest) {
				const replyPath = memberChatEndpointContract.replyChat.path.replace(
					":memberId",
					String(memberId),
				);
				const sentMessage = await $fetch<
					typeof memberChatEndpointContract.replyChat.responseType
				>(replyPath, {
					method: memberChatEndpointContract.replyChat.method,
					body: data,
				});
				const currentChats = $store?.atoms?.$chatMessages.get();
				if (currentChats && sentMessage.data?.chat) {
					$store?.atoms?.$chatMessages.set([
						...currentChats,
						sentMessage.data.chat,
					]);
				}
				return sentMessage;
			},
		},
		querySets, // Expose the raw querySets with proper typing
	};
}
